import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { User } from '../../../models/User';
import { ToastService } from '../../../shared/components/toast/toast.service';
import { AuthService } from '../../../shared/services/auth.service';
import { environment } from './../../../../environments/environment';
import { Integration } from './../../../models/Integration';
import { Lead } from './../../../models/Lead';
import { LeadList } from './../../../models/LeadList';
import { PlatformEvent } from './../../../models/PlatformEvent';
import { PostbackTokenState } from './../../../models/PostbackToken';
import { CopyService } from './../../../shared/services/copy.service';
import { ListsService } from './../../../shared/services/lists.service';
import { PostbackTokenService, listPostbackUrl } from './../../../shared/services/postback-token.service';
import { MenuService } from './../../layout/menu/menu.service';
import {
  IMPORT_SUMMARY_ICONS,
  ImportSummaryVariant,
  buildImportSummaryMessage,
  dismissImportSummary,
  importSummaryVariant,
  isImportSummaryDismissed,
} from './../../../shared/utils/import-summary';

@Component({
  selector: 'app-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss']
})
export class ListComponent implements OnInit, OnDestroy {

  title = 'Nova lista';

  file: File;
  user: User;
  leads: Lead[];
  leadList: LeadList;
  listId: string;
  listType: string;
  formList: UntypedFormGroup;
  integrations: Integration[];
  selectedEvent: PlatformEvent;

  dynamic = false;

  reloadLeads = new EventEmitter<boolean>();
  subscriptions: Subscription[] = [];

  /**
   * Carregamento no próprio botão (CU-86akkqppg): este formulário está sob
   * `/lists`, que saiu do overlay global, e o submit precisa de sinal próprio.
   */
  saving = false;

  isUserValidated: boolean = false;

  // Apresentação (M9 / achado A3 do PLANO.md): em `/lists/edit/:id` a tela de
  // escolha de tipo aparecia piscando enquanto `findById` não respondia, e
  // ficava presa nela para sempre se ele falhasse (sem nenhum aviso). A chamada
  // continua a mesma; só o que aparece durante a espera e no erro mudou —
  // esqueleto em vez da tela de tipo, e um card de erro com "Tentar de novo"
  // em vez de ficar preso (achado do QA do revisor, 14/10).
  loadListError = false;

  get loadingList(): boolean {
    return !!this.listId && !this.leadList && !this.loadListError;
  }

  // Bloco "Campos integração" (antes um tooltip de 500px, que estourava no
  // celular) virou recolhível — mesmo conteúdo, só a forma de mostrar.
  showFields = false;

  /**
   * Aviso do resumo do último import (CU-17tm466pyc5). Recalculado a cada vez
   * que a lista chega (findById, importFinished do polling, resposta do import);
   * `null` = não mostrar (sem resumo, importando ou fechado para este import).
   */
  importSummaryMessage: string | null = null;
  /**
   * Cor e ícone do aviso: erro (planilha recusada), atenção (lote falho ou nenhum
   * contato importado) ou sucesso.
   */
  importSummaryVariant: ImportSummaryVariant = 'success';
  readonly importSummaryIcons = IMPORT_SUMMARY_ICONS;

  postbackToken: string = null;
  postbackTokenState: PostbackTokenState = 'loading';

  @ViewChild('nameInput') nameInput: ElementRef<HTMLElement>;

  get integrationName(): string {
    if (this.leadList && this.leadList.integration) {
      return  `[${this.leadList.integration.platform.name}] ${this.leadList.integration.name}`;
    }
    return null;
  }

  get platformEventName(): string {
    if (
      this.leadList?.platform_events?.[0] &&
      typeof this.leadList.platform_events[0] === 'object'
    ) {
      return this.leadList.platform_events[0].description;
    }

    return null;
  }

  get isManual(): boolean {
    return this.formList.get('manual').value ? true : false;
  }

  get integrationUrl(): string {
    if (this.listId) {
      return listPostbackUrl(this.listId, this.postbackToken);
    }

    return null;
  }

  constructor(
    private router: Router,
    private authService: AuthService,
    private activetedRouter: ActivatedRoute,
    private formBuilder: UntypedFormBuilder,
    private toastService: ToastService,
    private listsService: ListsService,
    private copyService: CopyService,
    private menuService: MenuService,
    private postbackTokenService: PostbackTokenService,
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser();
    this.isUserValidated = this.user.validated; 

    // A URL de add-lead sai sem token até ele chegar; conta antiga não é obrigada
    // a usá-lo, então a URL nunca deixa de ser mostrada (CU-86ak8p7w0).
    this.subscriptions.push(
      this.postbackTokenService.get().subscribe(token => {
        this.postbackToken = token?.token ?? null;
        this.postbackTokenState = token ? 'ready' : 'unavailable';
      })
    );

    this.formList = this.formBuilder.group({
      id: [''],
      name: ['', [Validators.required, Validators.minLength(5)]],
      manual: [false],
      new_leads: [''],
      leads_count: [0],
      importing: [false],
      integration_id: [''],
      dynamic: [this.dynamic],
      tags: [''],
      platform_events: this.formBuilder.array([
        new UntypedFormControl(true)
      ], this.requiredMinChecked(1))
    });

    this.subscriptions.push(
      this.listsService.importFinished.subscribe(updateLeadList => {
        if (this.leadList?.id === updateLeadList.id) {
          this.loadList(updateLeadList);
        }
      })
    );

    this.subscriptions.push(
      this.activetedRouter.params.subscribe(params => {
        if (params.id) {
          this.listId = params.id;
          this.title = 'Alterar lista';
          this.findById();
        }
      })
    );

    if (!this.user.validated) {
      this.setListType('automática');
    }

    if (!this.listId) {
      this.setListType('manual');
    }
  }

  ngOnDestroy(): void {
      this.subscriptions.forEach(s => s.unsubscribe());
  }

  findById(): void {
    this.loadListError = false;
    this.subscriptions.push(
      this.listsService.findById(this.listId).subscribe({
        next: leadList => this.loadList(leadList),
        error: () => (this.loadListError = true),
      })
    );
  }

  toggleDynamic(): void {
    this.formList.patchValue({ dynamic: this.dynamic });
  }

  loadList(leadList: LeadList): void {
    this.listId = leadList.id;
    this.leadList = leadList;
    this.dynamic = leadList.dynamic;
    this.formList.patchValue(leadList);
    // Guarda contra platform_events ausente/vazio (achado ao rodar o Cypress
    // de verdade, 14/10) -- lista manual sem evento configurado não deve
    // quebrar o carregamento da tela.
    this.selectEvent(leadList.platform_events?.[0] as PlatformEvent);

    this.leads = leadList.leads;
    this.listType = leadList.manual ? 'manual' : 'automática';
    this.title = `Alterar lista ${this.listType}`;

    this.refreshImportSummary();

    this.reloadLeads.emit();
  }

  refreshImportSummary(): void {
    const summary = this.leadList?.import_summary;
    if (!summary || this.leadList.importing || !this.listId) {
      this.importSummaryMessage = null;
      this.importSummaryVariant = 'success';
      return;
    }

    this.importSummaryVariant = importSummaryVariant(summary);
    this.importSummaryMessage = isImportSummaryDismissed(this.listId, summary)
      ? null
      : buildImportSummaryMessage(summary);
  }

  dismissImportSummary(): void {
    const summary = this.leadList?.import_summary;
    if (summary && this.listId) {
      dismissImportSummary(this.listId, summary);
    }
    this.importSummaryMessage = null;
  }

  selectEvent(platformEvent: PlatformEvent): void {
    this.selectedEvent = platformEvent;
    this.formList.patchValue({ platform_events: [platformEvent] });
  }

  setListType(listType: string): void {
    this.listType = listType;
    this.title = (this.listId ? 'Alterar' : 'Nova') + ' lista';

    this.formList.removeControl('platform_events');

    if (listType === 'manual') {
      this.formList.patchValue({ manual: true, integration_id: '' });
      this.formList.addControl('platform_events', this.formBuilder.array([]));
    } else {
      this.formList.patchValue({ manual: false });
      this.formList.addControl('platform_events', this.formBuilder.array([], this.requiredMinChecked(1)));
    }

    this.formList.updateValueAndValidity();

    this.title += ' ' + listType;

    if (!this.menuService.isSmallDevice) {
      setTimeout(() => {
        this.nameInput?.nativeElement.focus();
      }, 200);
    }
  }

  requiredMinChecked(min: number = 1): any {
    const validator = (formArray: UntypedFormArray) => {

      if (this.formList?.get('manual').value) {
        return null;
      }

      const totalChecked = formArray.controls.map(v => v.value)
        .reduce((total, current) => current ? total + 1 : total, 0);

      return totalChecked >= min ? null : { required: true };
    };

    return validator;
  }

  copy(): void {
    this.copyService.copy('id', 'URL de para página web copiada!');
    const listName = this.formList.get('name').value;
  }

  updateList(leadList: LeadList): void {
    this.leadList = leadList;
    this.formList.patchValue(leadList);
    this.refreshImportSummary();
    this.listsService.addToStorage(leadList);
  }

  back(): void {
    history.back();
  }

  save(): void {

    let listData = Object.assign({}, this.formList.getRawValue());

    listData = Object.assign(listData, {
      platform_events: [this.selectedEvent]
    }) as LeadList;

    if (listData.manual === false && !listData.integration_id) {
      this.toastService.error('Selecione uma integração!');
      return;
    }

    // Achado do QA do revisor (14/10): com o campo Tags escondido (pedido do
    // Misael), uma lista antiga que já excedia 500 caracteres de tags ficava
    // impossível de salvar, mesmo sem o usuário poder mexer nelas pela tela.
    // Só barra quando as tags realmente mudaram em relação ao que veio do
    // findById — decisão registrada na task 17tm466p5vk (14/10/2026, achado do
    // revisor): sem o campo na tela, o usuario nao tem mais como mudar tags, entao
    // a validacao so se aplica quando chega um valor diferente do que ja estava salvo.
    const tagsChanged = JSON.stringify(listData.tags ?? []) !== JSON.stringify(this.leadList?.tags ?? []);
    if (tagsChanged && listData.tags?.length > 0) {
      const tags = listData.tags.join(',');
      if (tags.length > 500) {
        this.toastService.error('Você adicionou muitas TAGs. Você pode ter no máximo 500 caracteres de TAGs.');
        return;
      }
    }

    const successMessage = listData.id
      ? 'Lista alterada com sucesso!'
      : 'Lista cadastrada com sucesso!';

    const errorMessage = listData.id
      ? 'Erro ao alterar lista! Tente novamente mais tarde'
      : 'Erro ao cadastrar lista! Tente novamente mais tarde';

    this.saving = true;

    this.subscriptions.push(
      this.listsService.save(listData, this.file).subscribe(
        res => {
          this.saving = false;
          this.toastService.success(successMessage);
          this.listsService.addToStorage(res);
          if (!listData.id) {
            this.formList.reset();
            this.router.navigate(['/lists/edit', res.id]);
          }
        },
        err => {
          this.saving = false;
          const message = err.error?.message || errorMessage;
          this.toastService.error(message);
        }
      )
    );
  }
}
